
import {
  computed, defineComponent, defineExpose, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Loader from '@/components/layout/Loader.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { useRcsOnboarding } from '@/composables/rcs/useRcsOnboarding';
import WelcomeStep from '@/components/rcs-onboarding/steps/WelcomeStep.vue';
import AgentsListStep from '@/components/rcs-onboarding/steps/AgentsListStep.vue';
import RcsOnboardingWizard from '@/components/rcs-onboarding/RcsOnboardingWizard.vue';

/**
 * Thin shell mounted in the Service Parameter modal.
 *
 * Screens:
 *   - `welcome`     → service not activated yet (activate CTA)
 *   - `agents_list` → manage the shared Shopimind agent + dedicated agents
 *   - `wizard`      → create / edit a dedicated agent
 */
export default defineComponent({
  name: 'RcsServiceParameter',

  components: {
    Loader,
    Button,
    WelcomeStep,
    AgentsListStep,
    RcsOnboardingWizard,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },
    globalSave: {
      type: Boolean,
      required: false,
      default: false,
    },
    name: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup(props) {
    const { t } = useI18n();
    const onboarding = useRcsOnboarding(props.shopId);
    const isWizardOpen = ref(false);

    const screen = computed<'welcome' | 'agents_list' | 'wizard'>(() => {
      if (isWizardOpen.value) return 'wizard';
      if (!onboarding.status.value?.activated) return 'welcome';
      return 'agents_list';
    });

    const handleActivate = async () => {
      try {
        await onboarding.activate();
      } catch {
        showToastError('GENERIC_ERROR');
      }
    };

    const handleCreateAgent = () => {
      onboarding.startCreateAgent();
      isWizardOpen.value = true;
      onboarding.fetchAvailableCarriers();
    };

    const handleEditAgent = (localId: string) => {
      onboarding.startEditAgent(localId);
      isWizardOpen.value = true;
      onboarding.fetchAvailableCarriers();
    };

    const handleDeleteAgent = async (localId: string) => {
      try {
        await onboarding.deleteAgent(localId);
        showToastSuccess('savedSuccessful');
      } catch {
        showToastError('GENERIC_ERROR');
      }
    };

    const handlePushAgent = async (localId: string) => {
      try {
        await onboarding.pushAgentChanges(localId);
        showToastSuccess('savedSuccessful');
      } catch (err: any) {
        const code = err?.response?.data?.code;
        if (code === 'rcs_missing_required_fields') {
          showToastError(
            t('storeParameters.services.rcsServiceParameter.errors.incompleteAgent') as any,
          );
        } else if (code === 'rcs_sender_already_used') {
          showToastError(t('storeParameters.services.rcsServiceParameter.errors.senderAlreadyUsed') as any);
        } else {
          showToastError('GENERIC_ERROR');
        }
      }
    };

    const handleRetry = async (localId: string) => {
      try {
        await onboarding.retry(localId);
        showToastSuccess('savedSuccessful');
      } catch {
        showToastError('GENERIC_ERROR');
      }
    };

    const handleSubmitDedicated = async () => {
      try {
        await onboarding.submitDedicated();
        isWizardOpen.value = false;
        showToastSuccess('savedSuccessful');
      } catch (err: any) {
        const code = err?.response?.data?.code;
        if (code === 'rcs_sender_already_used') {
          showToastError(t('storeParameters.services.rcsServiceParameter.errors.senderAlreadyUsed') as any);
        } else if (code === 'rcs_brand_create_failed') {
          showToastError(
            t('storeParameters.services.rcsServiceParameter.errors.brandCreateFailed') as any,
          );
        } else {
          showToastError('GENERIC_ERROR');
        }
      }
    };

    const handleSaveAndContinue = async () => {
      const step = onboarding.currentWizardStep.value;
      try {
        if (step === 'basic_info') {
          await onboarding.saveBasicInfoDraft();
        } else if (step === 'carrier_selection') {
          await onboarding.saveCarrierSelectionDraft();
        } else if (step === 'visual') {
          await onboarding.saveVisualDesignDraft();
        } else if (step === 'representative') {
          await onboarding.saveRepresentativeDraft();
        } else if (step === 'carrier_requirements') {
          await onboarding.saveCarrierRequirementsDraft();
        } else if (step === 'test_numbers') {
          await onboarding.saveTestNumbersDraft();
        } else {
          onboarding.goNextWizardStep();
          return;
        }
        // Advance immediately; status refresh runs in the background inside save*Draft.
        onboarding.goNextWizardStep();
        showToastSuccess('savedSuccessful');
      } catch (err: any) {
        const code = err?.response?.data?.code;
        if (code === 'rcs_sender_already_used') {
          showToastError(t('storeParameters.services.rcsServiceParameter.errors.senderAlreadyUsed') as any);
        } else {
          showToastError('GENERIC_ERROR');
        }
      }
    };

    const handleWizardBack = async () => {
      if (onboarding.isFirstWizardStep.value) {
        isWizardOpen.value = false;
        await onboarding.refresh();
        return;
      }
      onboarding.goPreviousWizardStep();
    };

    // Exposed to satisfy the existing parent contract (`handleSave` is invoked
    // from the legacy modal save flow). RCS state is persisted through the
    // dedicated endpoints, so this is a no-op.
    const handleSave = async () => ({ err: null, configs: [] });

    defineExpose({ handleSave });

    onMounted(async () => {
      await onboarding.init();
    });

    return {
      t,
      onboarding,
      screen,
      handleActivate,
      handleCreateAgent,
      handleEditAgent,
      handleDeleteAgent,
      handleRetry,
      handlePushAgent,
      handleSubmitDedicated,
      handleSaveAndContinue,
      handleWizardBack,
      handleSave,
    };
  },
});
